// Alle checklists uit alle notities bij elkaar.
//
// Waarom dit scherm er is: je typt "[ ] offerte nabellen" in de notitie van een klant, en drie
// weken later weet je niet meer in wélke notitie dat stond. De taak staat daarom ook hier, met
// de notitie erbij, en je kunt hem hier afvinken zonder die notitie te openen.
//
// Afvinken is een gewone wijziging aan de notitie. Dat is met opzet: de versiegeschiedenis, de
// wijzigingenlijst en het synchroniseren werken dan precies zoals altijd, en er ontstaat geen
// tweede plek waar de waarheid zou kunnen staan.

import { useCallback, useEffect, useState } from "react";
import { api, GeenVerbinding, type Taak } from "../api.ts";
import { bewaarNotitie, takenUitSpiegel } from "../opslag.ts";
import { Icoon } from "./Icoon.tsx";
import { alsWoord } from "../../../server/kern/wanneer.ts";

type Stand = "laden" | "klaar" | "offline";

export function Taken({
  opNotitie,
  opMelding,
  opAantal
}: {
  opNotitie: (id: string) => void;
  opMelding?: (tekst: string) => void;
  /** Het aantal open taken, voor het getal in de zijbalk. */
  opAantal?: (aantal: number) => void;
}) {
  const [taken, setTaken] = useState<Taak[]>([]);
  const [stand, setStand] = useState<Stand>("laden");
  const [toon, setToon] = useState<"open" | "alles">("open");
  const [openAantal, setOpenAantal] = useState(0);

  const laad = useCallback(async () => {
    const af = toon === "open" ? false : undefined;
    try {
      const uit = await api.taken(af);
      setTaken(uit.taken);
      setStand("klaar");
      setOpenAantal(uit.open);
      opAantal?.(uit.open);
    } catch (fout) {
      if (!(fout instanceof GeenVerbinding)) throw fout;
      // Zonder netwerk uit de eigen kopie; dezelfde volgorde, dus de lijst verspringt niet
      // zodra je weer verbinding hebt.
      setTaken(await takenUitSpiegel(af));
      setStand("offline");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toon]);

  useEffect(() => {
    void laad();
  }, [laad]);

  async function wissel(taak: Taak) {
    // Meteen op het scherm; het wachten op de NAS hoort niet zichtbaar te zijn bij een vinkje.
    const eerder = taken;
    setTaken((rij) =>
      toon === "open"
        ? rij.filter((t) => !(t.notitie === taak.notitie && t.nr === taak.nr))
        : rij.map((t) => (t.notitie === taak.notitie && t.nr === taak.nr ? { ...t, af: !t.af } : t))
    );
    try {
      const uit = await api.zetTaakAf(taak.notitie, taak.nr, !taak.af, taak.tekst);
      await bewaarNotitie(uit.notitie);
      const nu = Math.max(0, openAantal + (taak.af ? 1 : -1));
      setOpenAantal(nu);
      opAantal?.(nu);
    } catch (fout) {
      setTaken(eerder);
      const status = (fout as { status?: number }).status;
      opMelding?.(
        status === 409
          ? "Die notitie is ondertussen veranderd — open hem even, dan zie je wat er nu staat."
          : fout instanceof GeenVerbinding
            ? "Geen verbinding — afvinken gaat pas als je weer online bent."
            : `Afvinken lukte niet: ${(fout as Error).message}`
      );
    }
  }

  // Bij elkaar per notitie, zodat je ziet waar iets bij hoort in plaats van twintig losse
  // regels die allemaal ergens vandaan komen.
  const groepen: { notitie: string; titel: string; taken: Taak[] }[] = [];
  for (const taak of taken) {
    const laatste = groepen[groepen.length - 1];
    if (laatste && laatste.notitie === taak.notitie) laatste.taken.push(taak);
    else groepen.push({ notitie: taak.notitie, titel: taak.titel, taken: [taak] });
  }

  return (
    <div className="lijstinhoud">
      {/* De kolomkop zegt al "Taken" met het aantal erbij; hier staat alleen wat je ermee kunt. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 4px 2px" }}>
        <span style={{ flexGrow: 1 }} />
        <button className="knop stil" onClick={() => setToon(toon === "open" ? "alles" : "open")}>
          {toon === "open" ? "Ook afgevinkte" : "Alleen open"}
        </button>
        {/* Een eigen verversknop: die van de kolomkop haalt de notitielijst op, niet deze. */}
        <button className="knop stil" onClick={() => void laad()} aria-label="Taken vernieuwen" title="Vernieuwen">
          <Icoon naam="ververs" grootte={15} />
        </button>
      </div>

      {stand === "offline" && (
        <p style={{ margin: "0 4px 6px", fontSize: 12, color: "var(--gedempt)" }}>
          Uit de kopie op dit apparaat — afvinken kan pas weer als je verbinding hebt.
        </p>
      )}

      {stand !== "laden" && taken.length === 0 && (
        <p className="leeg">
          {toon === "open" ? "Niets meer te doen." : "Nog geen taken. Typ in een notitie " + "[] " + "voor een vakje."}
        </p>
      )}

      {groepen.map((groep) => (
        <div key={groep.notitie} className="groep">
          <button className="groep-kop takenkop" onClick={() => opNotitie(groep.notitie)}>
            <Icoon naam="notitie" grootte={13} />
            {groep.titel || "Zonder titel"}
          </button>
          {groep.taken.map((taak) => (
            <div key={taak.nr} className="taakregel" style={{ paddingLeft: 10 + taak.diepte * 18 }}>
              <button
                className={`taakvak${taak.af ? " af" : ""}`}
                role="checkbox"
                aria-checked={taak.af}
                aria-label={taak.af ? "Weer openzetten" : "Afvinken"}
                onClick={() => void wissel(taak)}
              >
                {taak.af && <Icoon naam="vink" grootte={13} />}
              </button>
              <span className={taak.af ? "taakaf" : undefined} onClick={() => opNotitie(taak.notitie)}>
                {taak.tekst}
                {/* De datum als los kaartje: rood zodra hij verlopen is, want dan is het geen
                    aankondiging meer maar een achterstand. */}
                {taak.wanneer && (
                  <span className={`taakwanneer${new Date(taak.wanneer) <= new Date() ? " telaat" : ""}`}>
                    {alsWoord(taak.wanneer)}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
